import { useTranslation } from "react-i18next";
import { isCalledOffStatus } from "../../workoutStatus";

import type { WorkoutItem } from "../../planApi";

interface PhaseSpec {
  name: string;
  start: string; // ISO yyyy-mm-dd
  end: string;
}

interface RiskItem {
  key: string;
  severity: "info" | "warning" | "danger";
  title: string;
  summary: string;
}

interface Props {
  /** Workouts for the visible week (already filtered upstream). */
  items: ReadonlyArray<WorkoutItem>;
  phases?: ReadonlyArray<PhaseSpec>;
  /** Reference ISO date used to pick the active phase (defaults to today). */
  todayISO: string;
  risks?: ReadonlyArray<RiskItem>;
}

/**
 * PlanWeekSidebar — canon right rail of the athlete Plan page.
 *
 * Canon: docs/design/claude-design/desktop/athlete/Storica Desktop - Plan.dc.html
 *        section sidebar (P0.2 P2).
 *
 * Three stacked cards:
 *   • Week summary  — TSS / hours / km / disciplines
 *   • Phase progress — active phase, day N of M, progress bar
 *   • Risks         — count + first risk summary (warn-tinted card)
 *
 * Templates section (4th canon card) is deferred — out of P2 scope.
 */
export function PlanWeekSidebar({
  items,
  phases = [],
  todayISO,
  risks = [],
}: Props) {
  const { t } = useTranslation("plan");

  const summary = summariseWeek(items);
  const phase = activePhase(phases, todayISO);
  const phaseProgress = phase ? phaseProgressFor(phase, todayISO) : null;

  return (
    <aside
      data-testid="plan-week-sidebar"
      className="flex flex-col gap-3.5 w-full"
    >
      <div
        data-testid="plan-week-summary"
        className="rounded-card bg-bg-card p-4 space-y-3"
      >
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
          {t("plan_sidebar.summary_eyebrow", { defaultValue: "СВОДКА НЕДЕЛИ" })}
        </div>
        <ul className="space-y-2.5">
          <SummaryRow
            label={t("plan_sidebar.tss_label", { defaultValue: "TSS · общий" })}
            value={String(summary.tss)}
            tone="accent"
          />
          <SummaryRow
            label={t("plan_sidebar.hours_label", { defaultValue: "Объём" })}
            value={summary.hoursLabel}
            unit={t("plan_sidebar.hours_unit", { defaultValue: "ч" })}
          />
          <SummaryRow
            label={t("plan_sidebar.km_label", { defaultValue: "Километраж" })}
            value={String(Math.round(summary.km))}
            unit={t("plan_sidebar.km_unit", { defaultValue: "км" })}
          />
          <SummaryRow
            label={t("plan_sidebar.disciplines_label", { defaultValue: "Дисциплин" })}
            value={String(summary.disciplines)}
          />
        </ul>
      </div>

      {phase && phaseProgress ? (
        <div
          data-testid="plan-week-phase"
          className="rounded-card bg-bg-inner p-4 space-y-2.5"
        >
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-accent rounded-sm" aria-hidden />
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-accent">
              {phase.name.toUpperCase()} ·{" "}
              {t("plan_sidebar.phase_day_count", {
                defaultValue: "{{day}}/{{total}} ДН",
                day: phaseProgress.dayIndex,
                total: phaseProgress.totalDays,
              })}
            </span>
          </div>
          <div
            data-testid="plan-week-phase-progress"
            className="relative h-1.5 bg-bg-card rounded-full overflow-hidden"
          >
            <div
              className="absolute left-0 top-0 h-full bg-accent rounded-full"
              style={{ width: `${Math.round(phaseProgress.pct * 100)}%` }}
            />
          </div>
          <p className="text-[12px] text-fg-secondary leading-snug">
            {t("plan_sidebar.phase_hint", {
              defaultValue:
                "Фаза идёт по графику. Сложные дни внутри недели держим в плановом коридоре.",
            })}
          </p>
        </div>
      ) : null}

      {risks.length > 0 ? (
        <div
          data-testid="plan-week-risks"
          className="rounded-card p-4"
          style={{
            background: "rgba(var(--warn) / 0.06)",
            boxShadow: "inset 0 0 0 1px rgba(var(--warn) / 0.18)",
          }}
        >
          <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-warn mb-2">
            {t("plan_sidebar.risks_eyebrow", {
              defaultValue: "РИСКИ ПЛАНА · {{n}}",
              n: risks.length,
            })}
          </div>
          <div className="space-y-2">
            {risks.slice(0, 3).map((r) => (
              <div key={r.key} data-testid={`plan-week-risk-${r.key}`}>
                <div className="text-[12.5px] font-semibold text-fg-primary leading-tight">
                  {r.title}
                </div>
                <div className="text-[11.5px] text-fg-secondary leading-snug mt-1">
                  {r.summary}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </aside>
  );
}

function SummaryRow({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: string;
  unit?: string;
  tone?: "accent";
}) {
  return (
    <li className="flex items-baseline justify-between gap-2">
      <span className="text-[12.5px] text-fg-secondary leading-tight">{label}</span>
      <span className="flex items-baseline gap-1">
        <span
          className={[
            "text-base font-bold tabular-nums leading-none",
            tone === "accent" ? "text-accent" : "text-fg-primary",
          ].join(" ")}
        >
          {value}
        </span>
        {unit ? (
          <span className="text-[9px] font-mono font-bold tracking-[0.10em] text-fg-muted">
            {unit}
          </span>
        ) : null}
      </span>
    </li>
  );
}

interface Summary {
  tss: number;
  km: number;
  hoursLabel: string;
  disciplines: number;
}

export function summariseWeek(items: ReadonlyArray<WorkoutItem>): Summary {
  let tss = 0;
  let km = 0;
  let minutes = 0;
  const sports = new Set<string>();
  for (const w of items) {
    // Отменённая/перенесённая тренировка не должна раздувать объём недели
    // своим planned_load: её не будет, а сводка обещала бы нагрузку.
    if (isCalledOffStatus(w.status)) continue;
    // W-REBUILD-2: у выполненных тренировок planned_load часто пуст —
    // берём факт (actual_load), иначе сводка жила на нулях.
    const load =
      w.status === "completed" || w.status === "done"
        ? w.actual_load ?? w.planned_load ?? 0
        : w.planned_load ?? 0;
    tss += Number(load);
    const dist = w.actual_distance_km ?? w.planned_distance_km ?? 0;
    km += Number(dist);
    const dur = w.actual_duration_minutes ?? w.planned_duration_minutes ?? 0;
    minutes += Number(dur);
    if (w.discipline) sports.add(String(w.discipline).toLowerCase());
  }
  const hours = minutes / 60;
  const hoursLabel = hours >= 10 ? hours.toFixed(0) : hours.toFixed(1);
  return {
    tss: Math.round(tss),
    km,
    hoursLabel,
    disciplines: sports.size,
  };
}

export function activePhase(
  phases: ReadonlyArray<PhaseSpec>,
  refISO: string,
): PhaseSpec | null {
  for (const p of phases) {
    if (refISO >= p.start && refISO <= p.end) return p;
  }
  return null;
}

interface PhaseProgress {
  dayIndex: number;
  totalDays: number;
  pct: number;
}

export function phaseProgressFor(phase: PhaseSpec, refISO: string): PhaseProgress | null {
  const startDate = parseISO(phase.start);
  const endDate = parseISO(phase.end);
  const refDate = parseISO(refISO);
  if (!startDate || !endDate || !refDate) return null;
  const totalDays = Math.max(1, daysBetween(startDate, endDate) + 1);
  const dayIndex = Math.min(totalDays, Math.max(1, daysBetween(startDate, refDate) + 1));
  return { dayIndex, totalDays, pct: dayIndex / totalDays };
}

function parseISO(iso: string): Date | null {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function daysBetween(a: Date, b: Date): number {
  const ms = b.getTime() - a.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}
